import { useState, useRef, useEffect, useCallback } from 'react';
import useDebouncedFunc from '../../../Hooks/useDebouncedFunc.js';
import { SelectStates } from '../../FieldControlled/common.js';
import Log from '../../../../utils/log.js';

/* eslint-disable no-param-reassign */
const NO_ITEMS = [];
const LOADED = {
    state: undefined, message: undefined, items: NO_ITEMS, moreItems: false,
};
function isSameLoadingParameters(lastLoading, paging, searchTerm) {
    return !lastLoading.forceReload
        && lastLoading.lastPageSize === paging.pageSize
        && lastLoading.lastPage === paging.page
        && lastLoading.lastSearchTerm === searchTerm;
}
function loadItemsAndChangeState(onLoadItems, setItemsLoadingState, onLoaded, onCompleted) {
    setItemsLoadingState((s) => (Object.assign(Object.assign({}, s), { state: SelectStates.loading, message: undefined })));
    onLoadItems()
        .then((items) => {
        if (!items) {
            Log.warn('Verifica la chiamata onLoadItems');
            items = [];
        }
        setItemsLoadingState(() => (Object.assign(Object.assign({}, LOADED), { items, moreItems: false })));
        onLoaded(items[0]);
    })
        .catch((error) => {
        setItemsLoadingState(() => (Object.assign(Object.assign({}, LOADED), { items: [], state: SelectStates.alarm, message: error.message })));
    })
        .finally(onCompleted);
}
function loadItemsByPaginationAndChangeState(onLoadItems, setItemsLoadingState, paging, onLoaded, onCompleted) {
    setItemsLoadingState((s) => (Object.assign(Object.assign({}, s), { state: SelectStates.loadingPage, message: undefined })));
    onLoadItems()
        .then((itemsResponse) => {
        let items = itemsResponse.Items;
        if (!items) {
            Log.warn('Verifica la chiamata onLoadItems');
            items = [];
        }
        const moreItems = itemsResponse.Count > paging.pageSize * paging.page;
        const isFirstPage = paging.page === 1;
        setItemsLoadingState((s) => (Object.assign(Object.assign({}, LOADED), { items: isFirstPage ? items : [...s.items, ...items], moreItems })));
        onLoaded(items[0]);
    })
        .catch((error) => {
        setItemsLoadingState((s) => (Object.assign(Object.assign({}, LOADED), { items: s.items, state: SelectStates.alarm, message: error.message })));
    })
        .finally(onCompleted);
}
function useLoadItemsFunction(onLoadItems, paging, lastLoading, setItemsLoadingState) {
    const loadItems = useCallback(() => {
        var _a, _b, _c;
        const searchTerm = (_b = (_a = lastLoading.current.searchSession) === null || _a === void 0 ? void 0 : _a.getTerm()) !== null && _b !== void 0 ? _b : '';
        if (paging.page > 0) {
            if (isSameLoadingParameters(lastLoading.current, paging, searchTerm)) {
                (_c = lastLoading.current.searchSession) === null || _c === void 0 ? void 0 : _c.complete();
                return;
            }
            lastLoading.current.forceReload = false;
            lastLoading.current.lastPageSize = paging.pageSize;
            lastLoading.current.lastPage = paging.page;
            lastLoading.current.lastSearchTerm = searchTerm;
            const withPagination = !!paging.pageSize;
            if (withPagination) {
                const loadPaging = paging;
                loadItemsByPaginationAndChangeState(() => onLoadItems(searchTerm, loadPaging), setItemsLoadingState, loadPaging, (item) => { var _a; (_a = lastLoading.current.searchSession) === null || _a === void 0 ? void 0 : _a.notifyLoadCompleted(item !== null && item !== void 0 ? item : null); }, () => { var _a; (_a = lastLoading.current.searchSession) === null || _a === void 0 ? void 0 : _a.complete(); });
            }
            else {
                loadItemsAndChangeState(() => onLoadItems(searchTerm), setItemsLoadingState, (item) => { var _a; (_a = lastLoading.current.searchSession) === null || _a === void 0 ? void 0 : _a.notifyLoadCompleted(item !== null && item !== void 0 ? item : null); }, () => { var _a; (_a = lastLoading.current.searchSession) === null || _a === void 0 ? void 0 : _a.complete(); });
            }
        }
    }, [lastLoading, onLoadItems, paging, setItemsLoadingState]);
    return loadItems;
}
/**
 * Hook che si occupa del caricamento degli items della combobox. E' sua responsabilità anche la gestione del pageSize e dello scroll infinito
 * @param onLoadItems
 * @param debounceDelay
 * @param pageSize
 * @returns
 */
function useComboboxItems(onLoadItems, debounceDelay, pageSize) {
    const [itemsLoadingState, setItemsLoadingState] = useState(LOADED);
    const lastLoading = useRef({});
    const [paging, setPaging] = useState({ page: 0, pageSize });
    // solo se il searchTerm è differente dall'ultimo, allora viene forzato un ri-caricamento degli items
    const handleSearch = useDebouncedFunc((searchSession) => {
        var _a;
        if (!((_a = lastLoading.current.searchSession) === null || _a === void 0 ? void 0 : _a.equals(searchSession))) {
            lastLoading.current.searchSession = searchSession;
            setPaging((oldCurrentPage) => (Object.assign(Object.assign({}, oldCurrentPage), { page: 1 })));
        }
    }, debounceDelay, []);
    // al cambiamento del pageSize viene resettato il paging per forzare un ri-caricamento degli items
    useEffect(() => {
        lastLoading.current.lastPageSize = pageSize;
        setPaging({ pageSize, page: 1 });
    }, [pageSize]);
    // forzatura esterna (by ref) per il ri-caricamento degli items
    const reloadItems = useCallback(() => {
        lastLoading.current.forceReload = true;
        lastLoading.current.lastPageSize = 0;
        setPaging((oldCurrentPage) => (Object.assign(Object.assign({}, oldCurrentPage), { page: 1 })));
    }, []);
    // callback per caricare altri items (paginazione infinita)
    const loadMoreItems = useCallback(() => {
        setPaging((oldCurrentPage) => (Object.assign(Object.assign({}, oldCurrentPage), { page: oldCurrentPage.page + 1 })));
    }, []);
    const loadItems = useLoadItemsFunction(onLoadItems, paging, lastLoading, setItemsLoadingState);
    useEffect(() => {
        loadItems();
    }, [loadItems]);
    return {
        items: itemsLoadingState.items,
        reloadItems,
        handleSearch,
        stateItems: itemsLoadingState.state,
        messageItems: itemsLoadingState.message,
        moreItems: itemsLoadingState.moreItems,
        loadMoreItems,
    };
}

export { useComboboxItems as default };
